import { useState } from 'react'
import { useHistory } from 'react-router'
import OnPremAgentClient from '~/api/OnPremAgentClient/OnPremAgentClient'
import useOnPremAgentClient from '~/api/OnPremAgentClient/useOnPremAgentClient'
import { DataIngestor } from '~/api/onPremAgent/dataIngestorsAPI'
import useOnPremAgentQuery from '~/api/onPremAgent/useOnPremAgentQuery'
import { FullPageError } from '~/components/Errors'
import PopupMenu from '~/components/PopupMenu'
import Tabs from '~/components/Tabs'
import Toaster from '~/components/Toaster'
import ToggleSwitch from '~/components/ToggleSwitch'
import {
  getInstrumentCategory,
  getInstrumentImage,
  INSTRUMENT_DISPLAY_NAMES,
} from '~/utils/instrument'
import OnPremAgentBreadcrumbs from '../../components/OnPremAgentBreadcrumbs'
import ConfigureSettings from './ConfigureSettings'
import cs from './data_ingestors_page.scss'
import GenerateTestData from './GenerateTestData'
import ManualDatasetUpload from './ManualDatasetUpload'

export default function DataIngestorsPage({ ingestorUuid }: { ingestorUuid: string }) {
  let {
    loading,
    error,
    data: dataIngestor,
    setData: updateDataIngestor,
  } = useOnPremAgentQuery<null, DataIngestor>({
    backendQueryParams: {
      apiQuery: async (client: OnPremAgentClient) =>
        client.dataIngestors.getDataIngestor(ingestorUuid),
      apiParams: null,
    },
  })

  if (error) {
    return <FullPageError error={error} />
  }

  return (
    <div className={cs.dataIngestorsPage}>
      <div className={cs.topBar}>
        <div className={cs.controls}>
          <OnPremAgentBreadcrumbs ingestor={dataIngestor} />
        </div>
      </div>
      {!loading && dataIngestor && (
        <Content dataIngestor={dataIngestor} updateDataIngestor={updateDataIngestor} />
      )}
    </div>
  )
}

function Content(props: {
  dataIngestor: DataIngestor
  updateDataIngestor: (data: DataIngestor) => void
}) {
  const client = useOnPremAgentClient()
  const history = useHistory()
  const { dataIngestor, updateDataIngestor } = props
  const [toggleInProgress, setToggleInProgress] = useState(false)
  const [currentTab, setCurrentTab] = useState('configure_settings')

  const toggleDataIngestor = async () => {
    setToggleInProgress(true)
    try {
      if (dataIngestor.status === 'live') {
        await client.dataIngestors.stopDataIngestor(dataIngestor.uuid)
        updateDataIngestor({ ...dataIngestor, status: 'off' })
      } else {
        await client.dataIngestors.startDataIngestor(dataIngestor.uuid)
        updateDataIngestor({ ...dataIngestor, status: 'live' })
      }
    } catch (_e) {
      if (dataIngestor.status === 'live') {
        Toaster.show({
          message: 'Failed to stop data ingestor.',
          intent: 'danger',
        })
      } else {
        Toaster.show({
          message: 'Failed to start data ingestor.',
          intent: 'danger',
        })
      }
    }
    setToggleInProgress(false)
  }

  const deleteDataIngestor = async () => {
    try {
      await client.dataIngestors.deleteDataIngestor(dataIngestor.uuid)
      Toaster.show({
        message: `Deleted data ingestor ${dataIngestor.instrument_name}.`,
        intent: 'success',
      })
      history.push('/data-ingestors')
    } catch (_e) {
      Toaster.show({
        message: `Failed to delete data ingestor ${dataIngestor.instrument_name}.`,
        intent: 'danger',
      })
    }
  }

  const renderConfigureSettings = () => {
    return <ConfigureSettings dataIngestor={dataIngestor} />
  }

  const renderGenerateTestData = () => {
    return <GenerateTestData dataIngestor={dataIngestor} />
  }

  const renderManualDatasetUpload = () => {
    return <ManualDatasetUpload dataIngestor={dataIngestor} />
  }

  const manualUploadEnabled =
    dataIngestor.instrument_type === 'imagexpress_micro_confocal' ||
    dataIngestor.instrument_type === 'yokogawa_cq1'

  const tabs = [
    {
      key: 'configure_settings',
      title: 'Configure Settings',
      panel: renderConfigureSettings(),
    },
    {
      key: 'generate_test_data',
      title: 'Generate Test Data',
      panel: renderGenerateTestData(),
    },
  ]

  if (manualUploadEnabled) {
    tabs.push({
      key: 'manual_dataset_upload',
      title: 'Upload Dataset',
      panel: renderManualDatasetUpload(),
    })
  }

  const popupMenuOptions = [
    {
      label: 'Delete Data Ingestor',
      action: deleteDataIngestor,
    },
  ]

  return (
    <div className={cs.content}>
      <div className={cs.header}>
        <div className={cs.instrumentInfo}>
          {INSTRUMENT_DISPLAY_NAMES[dataIngestor.instrument_type]} &middot;{' '}
          {getInstrumentCategory({
            instrumentType: dataIngestor.instrument_type,
            instrumentName: '',
          })}
        </div>
        <div className={cs.instrumentNameContainer}>
          <img
            className={cs.instrumentImage}
            src={getInstrumentImage({
              instrumentType: dataIngestor.instrument_type,
              instrumentName: '',
            })}
          />
          <div className={cs.instrumentName}>{dataIngestor.instrument_name}</div>
          <PopupMenu
            options={popupMenuOptions}
            className={cs.menu}
            placement='bottom-end'
          />
          <ToggleSwitch
            onLabel='LIVE'
            offLabel='OFF'
            isOn={dataIngestor.status === 'live'}
            loading={toggleInProgress}
            onClick={toggleDataIngestor}
            className={cs.toggleSwitch}
            disabled={dataIngestor.status === 'paused'}
          />
        </div>
      </div>
      <Tabs
        tabs={tabs}
        initialTab='structured_data'
        className={cs.tabs}
        panelClassName={cs.tabPanel}
        onTabChange={setCurrentTab}
        selectedTabId={currentTab}
      />
    </div>
  )
}
